import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Home, ClipboardList, Banknote, CalendarDays, LogOut, Monitor, LineChart } from "lucide-react";
import ChurchFlowLogo from "@/components/ChurchFlowLogo";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const TABS = [
  { to: "/m", label: "Home", icon: Home, end: true, testid: "m-nav-home" },
  { to: "/m/treasurer", label: "Pulse", icon: LineChart, testid: "m-nav-treasurer" },
  { to: "/m/count", label: "Count", icon: ClipboardList, testid: "m-nav-count" },
  { to: "/m/cash", label: "Cash", icon: Banknote, testid: "m-nav-cash" },
  { to: "/m/services", label: "Services", icon: CalendarDays, testid: "m-nav-services" },
];

export default function MobileLayout() {
  const { user, logout, orgs, activeOrgId } = useAuth();
  const nav = useNavigate();
  const activeOrg = orgs.find((o) => o.id === activeOrgId);
  const [role, setRole] = useState(null);

  useEffect(() => {
    if (!activeOrgId) return;
    api.get(`/organizations/${activeOrgId}/my-role`).then(({ data }) => setRole(data.role)).catch(() => {});
  }, [activeOrgId]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="sticky top-0 z-20 bg-card/95 backdrop-blur border-b border-border">
        <div className="flex items-center justify-between px-4 py-3 max-w-2xl mx-auto w-full">
          <div className="flex items-center gap-2.5 min-w-0">
            <ChurchFlowLogo size={32} className="soft-glow rounded-xl shrink-0" />
            <div className="min-w-0">
              <div className="font-serif text-lg leading-none truncate">{activeOrg?.name || "ChurchFlow"}</div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5 truncate">
                {user?.name || user?.email} {role && <span>· {role}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {(role === "admin" || role === "treasurer" || !role) && (
              <Button variant="ghost" size="icon" className="rounded-xl" onClick={() => nav("/dashboard")} data-testid="m-desktop-link" title="Desktop view">
                <Monitor className="w-4 h-4" />
              </Button>
            )}
            <Button variant="ghost" size="icon" className="rounded-xl text-foreground/70 hover:text-destructive hover:bg-destructive/10" onClick={async () => { await logout(); nav("/login"); }} data-testid="m-logout-button">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-2xl w-full mx-auto px-4 pt-4 pb-28">
        <Outlet />
      </main>

      <nav className="fixed bottom-0 left-0 right-0 z-30 bg-card/95 backdrop-blur border-t border-border">
        <div className="max-w-2xl mx-auto grid grid-cols-5">
          {TABS.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              data-testid={t.testid}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center py-2.5 text-[11px] transition-colors ${
                  isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
                }`
              }
            >
              <t.icon className="w-5 h-5 mb-1" strokeWidth={2.2} />
              {t.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
